import React, { useEffect, useMemo, useReducer, useRef, useState } from "react";

import PreviewPanel from "./components/PreviewPanel.jsx";
import StepPanel from "./components/StepPanel.jsx";
import Stepper from "./components/Stepper.jsx";
import AttributesStep from "./steps/AttributesStep.jsx";
import BackgroundStep from "./steps/BackgroundStep.jsx";
import ClassStep from "./steps/ClassStep.jsx";
import IdentityStep from "./steps/IdentityStep.jsx";
import RaceStep from "./steps/RaceStep.jsx";
import StartingPointStep from "./steps/StartingPointStep.jsx";
import SubclassStep from "./steps/SubclassStep.jsx";
import SummaryStep from "./steps/SummaryStep.jsx";

import {
  ABILITY_KEYS,
  DATA_BASE,
  INITIAL_CREATOR_STATE,
  POINT_BUY_BUDGET,
  POINT_BUY_MAX,
  POINT_BUY_MIN,
  buildRolledSets,
  calcPointBuyCost,
  createStateSnapshot,
  getActiveStepIds,
  getBackgroundByName,
  getBackgroundGrantedFeat,
  getExtraFields,
  getFulfilledArray,
  getStepOrder,
  getStepTheme,
  getSummaryRows,
  isStepSelected,
  normalizeAttributeValue,
} from "./characterCreatorUtils.js";

function creatorReducer(state, action) {
  switch (action.type) {
    case "setStep":
      return { ...state, currentStepId: action.stepId };
    case "patch":
      return { ...state, ...action.patch };
    case "setRace":
      {
        const isCustomLineage = action.race?.name === "Linhagem Customizada";
        const shouldReuseCustomLineage = isCustomLineage && Boolean(state.customLineage);

        return {
          ...state,
          race: action.race,
          tashaAbilityOverride: { str: 0, dex: 0, con: 0, int: 0, wis: 0, cha: 0 },
          customLineage: isCustomLineage
            ? (shouldReuseCustomLineage
              ? state.customLineage
              : { lineageName: "", originFeat: "", skills: ["", ""], darkvision: false, size: "Medium" })
            : state.customLineage,
          humanRaceChoice: state.humanRaceChoice || { skill: "", feat: "" },
        };
      }
    case "setBackground":
      {
        const isCustomBackground = action.background === "Custom Background";
        const defaultCustomBackground = { skills: ["", ""], tool: "", originFeat: "", equipmentNotes: "" };

        return {
          ...state,
          background: action.background,
          backgroundStats: { str: 0, dex: 0, con: 0, int: 0, wis: 0, cha: 0 },
          backgroundStatMode: null,
          customBackground: isCustomBackground
            ? (state.customBackground || defaultCustomBackground)
            : state.customBackground,
        };
      }
    case "setOrigin":
      return { ...state, origin: action.origin };
    case "skipOrigin":
      return { ...state, origin: null };
    case "setClass":
      return { ...state, charClass: action.charClass, subclassData: null, subclass: "" };
    case "setSubclass":
      return { ...state, subclassData: action.subclass, subclass: action.subclass.name };
    case "setTashaAbility":
      return { ...state, tashaAbilityOverride: action.override };
    case "patchCustomLineage":
      return { ...state, customLineage: { ...state.customLineage, ...action.patch } };
    case "patchCustomBackground":
      return { ...state, customBackground: { ...state.customBackground, ...action.patch } };
    case "setStartingPoint":
      return {
        ...state,
        startingPoint: action.startingPoint.level,
        startingPointData: action.startingPoint,
      };
    case "setAttrMode":
      return { ...state, attrMode: action.mode };
    case "setAttributes":
      return { ...state, attributes: action.attributes };
    case "setAttribute":
      return {
        ...state,
        attributes: {
          ...state.attributes,
          [action.key]: normalizeAttributeValue(action.value),
        },
      };
    case "rollAttributeSets": {
      if (state.attrConfirmed) return state;
      if (state.attrMode !== null && state.attrMode !== "roll") return state;
      return {
        ...state,
        attrMode: "roll",
        rolledSets: action.sets,
        rollAssignments: Object.fromEntries(ABILITY_KEYS.map((k) => [k, null])),
      };
    }
    case "assignRollSlot": {
      if (state.attrConfirmed || state.attrMode !== "roll") return state;
      const newAssignRoll = { ...state.rollAssignments };
      Object.keys(newAssignRoll).forEach((k) => {
        if (newAssignRoll[k] === action.slotIndex) newAssignRoll[k] = null;
      });
      newAssignRoll[action.ability] = action.slotIndex;
      return { ...state, rollAssignments: newAssignRoll };
    }
    case "engageStandard": {
      if (state.attrConfirmed) return state;
      if (state.attrMode !== null && state.attrMode !== "standard") return state;
      const newAssignStd = { ...state.standardAssignments };
      Object.keys(newAssignStd).forEach((k) => {
        if (newAssignStd[k] === action.value) newAssignStd[k] = null;
      });
      newAssignStd[action.ability] = action.value;
      return { ...state, attrMode: "standard", standardAssignments: newAssignStd };
    }
    case "engagePointBuy": {
      if (state.attrConfirmed) return state;
      if (state.attrMode !== null && state.attrMode !== "pointbuy") return state;
      const newPBVals = { ...state.pointBuyValues };
      const next = (newPBVals[action.key] || POINT_BUY_MIN) + action.delta;
      if (next < POINT_BUY_MIN || next > POINT_BUY_MAX) return state;
      const testVals = { ...newPBVals, [action.key]: next };
      if (calcPointBuyCost(testVals) > POINT_BUY_BUDGET) return state;
      newPBVals[action.key] = next;
      return { ...state, attrMode: "pointbuy", pointBuyValues: newPBVals };
    }
    case "confirmAttributes": {
      if (state.attrConfirmed) return state;
      return {
        ...state,
        attributes: action.attributes,
        attrConfirmed: true,
        attrMode: state.attrMode || action.method,
      };
    }
    case "selectHpMethod": {
      if (!state.attrConfirmed || state.hpConfirmed) return state;
      return { ...state, hpMethodLocked: action.method, hpRolledValues: null };
    }
    case "rollHpDice": {
      if (!state.attrConfirmed || state.hpConfirmed) return state;
      return { ...state, hpMethodLocked: "roll", hpRolledValues: action.values };
    }
    case "confirmHpFixed": {
      if (!state.attrConfirmed || state.hpConfirmed) return state;
      return { ...state, hpMethodLocked: "max", hpRolledValues: null, hpConfirmed: true };
    }
    case "confirmHP": {
      if (!state.attrConfirmed || state.hpConfirmed) return state;
      return { ...state, hpConfirmed: true };
    }
    default:
      return state;
  }
}

async function fetchJSON(url) {
  const response = await fetch(url);
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return response.json();
}

export default function CharacterCreatorApp({ onComplete, onCancel, initialState } = {}) {
  const startState = initialState ?? INITIAL_CREATOR_STATE;
  const [state, dispatch] = useReducer(creatorReducer, startState);
  const [allRaces, setAllRaces] = useState(null);
  const [allClasses, setAllClasses] = useState(null);
  const [startingPoints, setStartingPoints] = useState(null);
  const [homebrewBackgrounds, setHomebrewBackgrounds] = useState([]);
  const [originFeats, setOriginFeats] = useState([]);
  const [exportType, setExportType] = useState("json");
  const [exportSnapshot, setExportSnapshot] = useState(() => createStateSnapshot(startState));
  const [mobilePreviewOpen, setMobilePreviewOpen] = useState(false);
  const [furthestUnlockedOrder, setFurthestUnlockedOrder] = useState(() => getStepOrder(startState.currentStepId));
  const wizardAreaRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    Promise.allSettled([
      fetchJSON(DATA_BASE + "races.json"),
      fetchJSON(DATA_BASE + "classes.json"),
      fetchJSON(DATA_BASE + "starting-points.json"),
      fetchJSON(DATA_BASE + "backgrounds.json"),
      fetchJSON(DATA_BASE + "feats.json"),
    ]).then(([racesResult, classesResult, startingPointsResult, backgroundsResult, featsResult]) => {
      if (cancelled) return;
      setAllRaces(getFulfilledArray(racesResult));
      setAllClasses(getFulfilledArray(classesResult));
      setStartingPoints(getFulfilledArray(startingPointsResult));
      setHomebrewBackgrounds(getFulfilledArray(backgroundsResult));
      setOriginFeats(getFulfilledArray(featsResult));
    });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (wizardAreaRef.current) wizardAreaRef.current.scrollTop = 0;
  }, [state.currentStepId]);

  useEffect(() => {
    setFurthestUnlockedOrder((currentMax) => Math.max(currentMax, getStepOrder(state.currentStepId)));
  }, [state.currentStepId]);

  const activeStepIds = getActiveStepIds(state);
  const currentIndex = activeStepIds.indexOf(state.currentStepId);
  const currentStepRequiresSelection = state.currentStepId === "raca" || state.currentStepId === "background";
  const canAdvanceCurrentStep = !currentStepRequiresSelection || isStepSelected(state.currentStepId, state);
  const extraFields = getExtraFields(state);
  const summaryRows = getSummaryRows(state);
  const selectedBackground = useMemo(
    () => getBackgroundByName(homebrewBackgrounds, state.background),
    [homebrewBackgrounds, state.background]
  );
  const activeTheme = useMemo(
    () => getStepTheme(state.currentStepId, state),
    [
      state.currentStepId,
      state.race,
      state.startingPointData,
      state.charClass,
      state.subclassData,
    ]
  );

  useEffect(() => {
    const body = document.body;
    if (!body) return;

    if (!activeTheme) {
      body.classList.remove("personagem-global-themed");
      body.style.removeProperty("--global-theme-image");
      body.style.removeProperty("--global-theme-accent");
      body.style.removeProperty("--global-theme-soft");
      body.style.removeProperty("--global-theme-glow");
      return;
    }

    const safeUrl = String(activeTheme.imageUrl || "").replace(/"/g, '\\"');
    body.style.setProperty("--global-theme-image", `url(\"${safeUrl}\")`);
    body.style.setProperty("--global-theme-accent", activeTheme.accent);
    body.style.setProperty("--global-theme-soft", activeTheme.soft);
    body.style.setProperty("--global-theme-glow", activeTheme.glow);
    body.classList.add("personagem-global-themed");
  }, [activeTheme]);

  useEffect(() => {
    return () => {
      const body = document.body;
      if (!body) return;
      body.classList.remove("personagem-global-themed");
      body.style.removeProperty("--global-theme-image");
      body.style.removeProperty("--global-theme-accent");
      body.style.removeProperty("--global-theme-soft");
      body.style.removeProperty("--global-theme-glow");
    };
  }, []);

  useEffect(() => {
    const nextBackgroundFeat = getBackgroundGrantedFeat(selectedBackground);
    const patch = {};

    if (state.backgroundFeat !== nextBackgroundFeat) {
      patch.backgroundFeat = nextBackgroundFeat;
    }

    if ((!state.feat1 || state.feat1 === state.backgroundFeat) && state.feat1 !== nextBackgroundFeat) {
      patch.feat1 = nextBackgroundFeat;
    }

    if (Object.keys(patch).length > 0) {
      dispatch({ type: "patch", patch });
    }
  }, [selectedBackground, state.backgroundFeat, state.feat1]);

  // S1.7: mantém exportSnapshot sincronizado enquanto o usuário está no resumo
  useEffect(() => {
    if (state.currentStepId === "resumo") {
      setExportSnapshot(createStateSnapshot(state));
    }
  }, [state]);

  function handleSetBackgroundStats({ stats, mode }) {
    dispatch({ type: "patch", patch: { backgroundStats: stats, backgroundStatMode: mode } });
  }

  function goToStep(stepId) {
    dispatch({ type: "setStep", stepId });
    if (stepId === "resumo") {
      setExportType("json");
      setExportSnapshot(createStateSnapshot(state));
    }
  }

  function goToNextStep() {
    if (currentIndex < activeStepIds.length - 1) {
      if (!canAdvanceCurrentStep) return;
      goToStep(activeStepIds[currentIndex + 1]);
    } else if (onComplete) {
      onComplete(state);
    }
  }

  function goToPreviousStep() {
    if (currentIndex > 0) goToStep(activeStepIds[currentIndex - 1]);
  }

  function handleRollSets(sets) {
    dispatch({ type: "rollAttributeSets", sets });
  }

  function handleAssignRollSlot(ability, slotIndex) {
    dispatch({ type: "assignRollSlot", ability, slotIndex });
  }

  function handleAssignStandard(ability, value) {
    dispatch({ type: "engageStandard", ability, value });
  }

  function handlePointBuyChange(key, delta) {
    dispatch({ type: "engagePointBuy", key, delta });
  }

  function handleConfirmAttributes(finalAttributes, method) {
    dispatch({ type: "confirmAttributes", attributes: finalAttributes, method });
  }

  function handleSelectHpMethod(method) {
    dispatch({ type: "selectHpMethod", method });
  }

  function handleRollHp(values) {
    dispatch({ type: "rollHpDice", values });
  }

  function handleConfirmHpFixed() {
    dispatch({ type: "confirmHpFixed" });
  }

  function handleConfirmHp() {
    dispatch({ type: "confirmHP" });
  }

  function updateExportType(nextType) {
    setExportType(nextType);
    setExportSnapshot(createStateSnapshot(state));
  }

  return (
    <>
      <div className="creator-shell">
        <PreviewPanel state={state} />
        <Stepper
          state={state}
          furthestUnlockedOrder={furthestUnlockedOrder}
          onStepChange={goToStep}
        />

        <section className="wizard-area" id="wizardArea" ref={wizardAreaRef}>
          {state.currentStepId === "identidade" && (
            <StepPanel
              active
              stepId="identidade"
              title="Identidade"
              description="Nomeie seu herói. O background será escolhido na próxima etapa."
            >
              <IdentityStep
                state={state}
                onPatch={(patch) => dispatch({ type: "patch", patch })}
              />
            </StepPanel>
          )}

          {state.currentStepId === "raca" && (
            <StepPanel
              active
              stepId="raca"
              title="Raça"
              description="Escolha a raça do seu personagem — raças do SRD e homebrew do mundo da campanha."
              theme={getStepTheme("raca", state)}
            >
              <RaceStep
                state={state}
                races={allRaces}
                originFeats={originFeats}
                onSelectRace={(race) => dispatch({ type: "setRace", race })}
                onPatchCustomLineage={(patch) => dispatch({ type: "patchCustomLineage", patch })}
                onPatchHumanRaceChoice={(patch) => dispatch({
                  type: "patch",
                  patch: { humanRaceChoice: { ...(state.humanRaceChoice || {}), ...patch } },
                })}
              />
            </StepPanel>
          )}

          {state.currentStepId === "background" && (
            <StepPanel
              active
              stepId="background"
              title="Background"
              description="O background define a história prévia do seu herói e concede bônus de atributos, proficiências e um feat inicial."
            >
              <BackgroundStep
                state={state}
                backgrounds={homebrewBackgrounds}
                originFeats={originFeats}
                onSelectBackground={(bg) => dispatch({ type: "setBackground", background: bg.name })}
                onSetBackgroundStats={handleSetBackgroundStats}
                onPatchCustomBackground={(patch) => dispatch({ type: "patchCustomBackground", patch })}
              />
            </StepPanel>
          )}

          {state.currentStepId === "nivel" && (
            <StepPanel
              active
              stepId="nivel"
              title="Nível Inicial"
              description="O DM define em que estágio da jornada seu herói entra na campanha."
              theme={getStepTheme("nivel", state)}
            >
              <StartingPointStep
                state={state}
                startingPoints={startingPoints}
                onSelectStartingPoint={(startingPoint) =>
                  dispatch({ type: "setStartingPoint", startingPoint })
                }
                onPatch={(patch) => dispatch({ type: "patch", patch })}
              />
            </StepPanel>
          )}

          {state.currentStepId === "classe" && (
            <StepPanel
              active
              stepId="classe"
              title="Classe"
              description="Escolha a classe que define o estilo de combate e habilidades do seu personagem."
              theme={getStepTheme("classe", state)}
            >
              <ClassStep
                state={state}
                classes={allClasses}
                onSelectClass={(charClass) => dispatch({ type: "setClass", charClass })}
              />
            </StepPanel>
          )}

          {state.currentStepId === "subclasse" && (
            <StepPanel
              active
              stepId="subclasse"
              title="⭐ Subclasse"
              description="Seu poder está evoluindo. Escolha o caminho que define sua especialização."
              theme={getStepTheme("subclasse", state)}
            >
              <SubclassStep
                state={state}
                onSelectSubclass={(subclass) => dispatch({ type: "setSubclass", subclass })}
              />
            </StepPanel>
          )}

          {state.currentStepId === "atributos" && (
            <StepPanel
              active
              stepId="atributos"
              title="Atributos"
              description="Defina os seis atributos base do personagem. Bônus de origem são aplicados automaticamente."
            >
              <AttributesStep
                state={state}
                onRollSets={handleRollSets}
                onAssignRollSlot={handleAssignRollSlot}
                onAssignStandard={handleAssignStandard}
                onPointBuyChange={handlePointBuyChange}
                onConfirmAttributes={handleConfirmAttributes}
                onSelectHpMethod={handleSelectHpMethod}
                onRollHp={handleRollHp}
                onConfirmHp={handleConfirmHp}
                onConfirmHpFixed={handleConfirmHpFixed}
              />
            </StepPanel>
          )}

          {state.currentStepId === "resumo" && (
            <StepPanel
              active
              stepId="resumo"
              title="Resumo"
              description="Revise a ficha completa e exporte-a para o formato desejado."
            >
              <SummaryStep
                summaryRows={summaryRows}
                extraFields={extraFields}
                exportType={exportType}
                exportSnapshot={exportSnapshot}
                onExportTypeChange={updateExportType}
                onExtraFieldChange={(field, value) =>
                  dispatch({ type: "patch", patch: { [field]: value } })
                }
              />
            </StepPanel>
          )}
        </section>

        <footer className="wizard-footer">
          <button
            type="button"
            className="wiz-btn"
            id="btnPrev"
            disabled={currentIndex === 0}
            onClick={goToPreviousStep}
          >
            ← Voltar
          </button>
          <button
            type="button"
            className="wiz-btn primary"
            id="btnNext"
            onClick={goToNextStep}
            disabled={currentIndex < activeStepIds.length - 1 && !canAdvanceCurrentStep}
          >
            {currentIndex === activeStepIds.length - 1 ? "✦ Concluir" : "Próximo →"}
          </button>
        </footer>
      </div>

      <button
        type="button"
        className="mobile-preview-toggle"
        id="mobilePreviewBtn"
        onClick={() => setMobilePreviewOpen(true)}
      >
        👤 Ver Preview
      </button>
      <div
        className={`mobile-preview-overlay${mobilePreviewOpen ? " open" : ""}`}
        id="mobilePreviewOverlay"
        onClick={(event) => {
          if (event.target === event.currentTarget) setMobilePreviewOpen(false);
        }}
      >
        <div style={{ position: "relative" }}>
          <button
            type="button"
            className="mobile-preview-close"
            id="mobilePreviewClose"
            onClick={() => setMobilePreviewOpen(false)}
          >
            ✕
          </button>
          <PreviewPanel state={state} isMobile />
        </div>
      </div>
    </>
  );
}
